'use strict';
/* ==========================================================================
   levels/video.js — step 1, SAY ANYTHING?: the interactive video (shared/engine/v2/core/player.js), print-shop
   collage, one scene per script segment (content/script.json s1–s5; each segment's "visual" is the storyboard).
   Modelled on lessons/branches/levels/video.js (same cast, same staging, same caption bubbles).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.E / ctx.W
   (via cues(), which turns the ±Infinity of other parts into ±1e6), so the choreography follows whatever the voice
   timing is (text timing now, the recorded clips later, with no code change) and the player can seek, replay and run
   at 25×. Branches: a reply cue is min() over the question's reply ids (branch()): in the reply part only the branch
   that plays is finite, in the lines part all are +∞ (not yet), in the after part all are −∞ (done).

   Honesty (DESIGN.md §4): every case name, year and vote on screen comes from core-local/rights.js (Rights.CASES:
   tinker, hazelwood, schenck, brandenburg, gideon, miranda, gault) and the First Amendment's words from Rights.RIGHTS
   (the archives.gov transcript, facts 2.1). Local storyboard facts, each with its facts-free-speech.md row:
   the Tinker quote (4.3), the Schenck quote (5.7), 1965 and Mary Beth Tinker's age 13 (4.2), 1791 (2.0), the ten
   amendments' kid lines (2.1–2.10; 4 and 8 from Rights.RIGHTS), the Hazelwood / Gideon / Miranda / Gault docket
   lines (5.11, 6.4, 6.2, 6.3). The small Florida silhouette is traced from Natural Earth 1:110m
   (lessons/ground/assets/data/coast.json, public domain), closed along the state line (approximate) — decoration.

   Staging (like lessons/branches): each segment opens on a chapter card that tears away; Rex (left) and the Judge
   (right) stand full size ("two": captions in the top band on the speaker's side) or shrink to bottom-corner insets
   ("inset": captions in the bottom band beside them) when the boards need the screen. Boards stay inside
   y 60…790 in inset mode (the bubble band and the player's controls below). Portrait: the player puts the captions in
   the dock; the First Amendment, the quotes and the dockets are also written into the dock (Dock.text).
   Art: Rex / Judge (core-local/hooks.js poses), capitol / court / gavel / quill (Civic.building / Civic.tool), the
   engraving props via Civic.prop (armband, newspaper, megaphone, scroll_rights, printing_press): each drawn only if
   loaded, else a paper stand-in. SFX: shared + press; unroll / crowd_murmur / gavel / cheer only if present.
   Debug / QA: window.FreeSpeechVideo (BODY, TEXT, layers, lastDock).
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'", TYPE = "'Special Elite'", SLAB = "'Alfa Slab One'", SERIF = "'Abril Fatface'";
  const INK = '#2b2420', PENCIL = 'rgba(43,36,32,0.62)', RED = '#b3261e', GREEN = '#2e5a4f', GOLD_INK = '#7d5f14';
  const KC = () => Civic.C;
  const TITLE_PAL = [['#1f2f55', '#f3ead7'], ['#9e2b25', '#f3ead7'], ['#f3ead7', '#1f2f55'], ['#fffdf7', '#9e2b25'], ['news', '#2b2420'], ['#b08a2e', '#2b2420']];
  const CASE = id => Rights.CASES[id];
  const VOTE = v => String(v).replace('-', '–');

  /* ---------------- storyboard text (facts-free-speech.md rows) ---------------- */
  const TEXT = {
    tinkerQuote: 'It can hardly be argued that either students or teachers shed their constitutional rights to freedom of speech or expression at the schoolhouse gate.',   // 4.3
    schenckQuote: 'The most stringent protection of free speech would not protect a man in falsely shouting fire in a theatre and causing a panic.',                         // 5.7
    armbandYear: 1965, maryBethAge: 13,                                                                                                                                 // 4.2
    borYear: 1791,                                                                                                                                                      // 2.0
  };
  /* the First Amendment (facts 2.1), assembled from the model's clause texts */
  function amend1() {
    const R = Rights.RIGHTS;
    return `${R.religion.text}; ${R.speech.text}, ${R.press.text}; ${R.assembly.text}, ${R.petition.text}.`;
  }
  const HEADLINE = 'Congress shall make no law';
  /* the ten amendments in a line each (facts 2.1–2.10; the kid lines, shortened) */
  const TEN = () => [
    'Religion, speech, press, assembly, petition.',                         // 2.1
    'The right of the people to keep and bear arms.',                       // 2.2 (quote only)
    'No soldiers moved into your house in peacetime.',                      // 2.3
    Rights.RIGHTS.search.kid,                                               // 2.4
    'Not tried twice, not forced to testify; due process.',                 // 2.5
    'A quick, public trial, a fair jury, and a lawyer.',                    // 2.6
    'In many lawsuits over money, you can have a jury.',                    // 2.7
    Rights.RIGHTS.cruel.kid,                                                // 2.8
    "A right that isn't on the list can still be yours.",                   // 2.9
    'Other powers belong to the states or the people.',                     // 2.10
  ];
  const ORD = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];

  /* ---------------- small helpers (as lessons/branches) ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const fx = (name, alt) => (Audio.has(name) ? name : alt);
  const ansOf = (ctx, part) => { const q = part.seg.question; if (!q) return null; return ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct; };
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  function wrapLines(str, maxW, size, font = HANDF) {
    const words = String(str).split(/\s+/), out = []; let cur = '';
    for (const w of words) { const nx = cur ? cur + ' ' + w : w; if (cur && tw(nx, `${size}px ${font}`) > maxW) { out.push(cur); cur = w; } else cur = nx; }
    if (cur) out.push(cur);
    return out;
  }
  /* the largest size ≤ size at which str fits maxW */
  function fitSize(str, maxW, size, font = HANDF, min = 14) { let s = size; while (s > min && tw(str, `${s}px ${font}`) > maxW) s -= 1; return s; }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  function txt(str, x, y, size, o = {}) {
    g.save(); g.font = `${size}px ${o.font || HANDF}`; g.fillStyle = o.color || INK; g.textAlign = o.align || 'center'; g.textBaseline = 'middle';
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    if (o.rot) { g.translate(x, y); g.rotate(o.rot); g.fillText(str, 0, 0); } else g.fillText(str, x, y);
    g.restore();
  }
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v == null) continue; const [w, n] = Array.isArray(v) ? v : [v, 1]; best = Math.min(best, c.W(id, w, n)); }
    return best;
  }
  const replyIds = q => q.right.concat(...Object.values(q.wrong || {}));
  const replyStart = (c, q) => Math.min(...replyIds(q).map(id => c.L(id)));
  /* the same word on every branch of a question */
  const every = (q, word) => { const o = {}; replyIds(q).forEach(id => { o[id] = word; }); return o; };
  function track(t, keys, dur = 0.6) {
    let x = keys[0][1], y = keys[0][2], s = keys[0][3] ?? 1;
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i]; if (t <= k[0]) continue;
      const e = E.inOutCubic(P(t, k[0], k[0] + (k[4] ?? dur)));
      x = lerp(x, k[1], e); y = lerp(y, k[2], e); s = lerp(s, k[3] ?? s, e);
    }
    return { x, y, s };
  }
  /* draw fn about (x, y) at scale s (fn draws in its own unscaled coordinates around cx, cy) */
  function at(x, y, s, cx, cy, fn, rot = 0) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s); g.translate(-cx, -cy); try { fn(); } finally { g.restore(); }
  }
  /* a docket sheet for case K (Rights.CASES) at scale s (Civic.docket's seal and EOC badge follow its `s`) */
  function docketAt(x, y, s, w, h, K, o = {}) {
    Civic.docket(x, y, w, h, { name: K.name, year: K.year, vote: VOTE(K.vote), eoc: o.eoc ?? K.eoc, lines: o.lines || [], s, rot: o.rot || 0, seed: o.seed || 41, t0: o.t0 ?? -1e6 });
  }
  const dock = (key, lines) => { if (UI.docked()) { Dock.text(key, lines, { cls: 'note' }); FreeSpeechVideo.lastDock = key; } };

  /* ---------------- layers: static art baked once (the engine's OFF budget evicts them like sprites) ---------------- */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }

  /* ---------------- backdrops (baked) ---------------- */
  /* s1: the print shop — kraft wall, type-case drawers, a work bench */
  function bgShop() {
    layer('fs_shop', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
      for (let r = 0; r < 3; r++) for (let k = 0; k < 9; k++) {
        const x = 150 + k * 205, y = 250 + r * 150;
        paperRect(x, y, 186, 128, '#8a6a47', { seed: 200 + r * 9 + k, depth: 0.5, boil: false });
        paperRect(x, y, 160, 102, '#6e5238', { seed: 230 + r * 9 + k, depth: 0, boil: false });
        paperRect(x, y - 30, 40, 12, '#c9a25a', { seed: 260 + r * 9 + k, depth: 0, boil: false });
      }
      paperRect(960, 960, 2060, 260, '#5a4632', { seed: 290, torn: true, depth: 1.2, boil: false });
      for (let i = 0; i < 8; i++) ink([[0, 880 + i * 22], [1920, 884 + i * 22]], { w: 1.6, color: 'rgba(30,20,10,0.25)', seed: 291 + i, passes: 1, step: 120 });
      texScrap('tex_news', 1760, 120, 320, 200, { rot: 0.07, seed: 299, depth: 0.6, tint: C.parchment, tintAlpha: 0.5 });
      texScrap('tex_news', 170, 110, 300, 180, { rot: -0.06, seed: 298, depth: 0.6, tint: C.parchment, tintAlpha: 0.5 });
    }, { zoom: 0.75 });
  }
  /* s2: the school's front gate — sky, brick wall, iron gate, the school's name */
  function bgGate() {
    layer('fs_gate', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile('#dfe6e4', 5); g.fillRect(0, 0, 1920, 1080);
      paperRect(960, 820, 2060, 560, '#b6694f', { seed: 310, torn: true, depth: 1.2, boil: false });
      for (let r = 0; r < 9; r++) {
        ink([[0, 560 + r * 58], [1920, 562 + r * 58]], { w: 2, color: 'rgba(80,30,20,0.35)', seed: 311 + r, passes: 1, step: 120 });
        for (let k = 0; k < 16; k++) { const x = k * 128 + (r % 2) * 64; ink([[x, 560 + r * 58], [x + 2, 618 + r * 58]], { w: 2, color: 'rgba(80,30,20,0.3)', seed: 330 + r * 16 + k, passes: 1, step: 40 }); }
      }
      // gate posts + bars
      [640, 1280].forEach((x, i) => paperRect(x, 560, 90, 900, '#9c5741', { seed: 360 + i, depth: 1.3, boil: false }));
      for (let k = 0; k < 11; k++) ink([[700 + k * 52, 150], [700 + k * 52, 1000]], { w: 7, color: 'rgba(30,30,34,0.85)', seed: 370 + k, passes: 1, step: 80 });
      ink([[690, 180], [1230, 180]], { w: 8, color: 'rgba(30,30,34,0.85)', seed: 385, passes: 1, step: 80 });
      ink([[690, 880], [1230, 880]], { w: 8, color: 'rgba(30,30,34,0.85)', seed: 386, passes: 1, step: 80 });
      paperRect(960, 100, 760, 84, C.navy, { seed: 390, depth: 1.2, boil: false });
      g.save(); g.font = `46px ${SLAB}`; g.fillStyle = C.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('CORAL RIDGE MIDDLE', 960, 104); g.restore();
    }, { zoom: 0.75 });
  }
  /* s3: the newsroom — parchment wall with pinned newsprint */
  function bgNews() {
    layer('fs_news', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile(C.parchment, 7); g.fillRect(0, 0, 1920, 1080);
      [[180, 200, -0.08], [1740, 220, 0.06], [220, 860, 0.05], [1720, 880, -0.05], [960, 90, 0.02]].forEach(([x, y, r], i) =>
        texScrap('tex_news', x, y, 380, 260, { rot: r, seed: 400 + i, depth: 0.7, tint: C.parchment, tintAlpha: 0.45 }));
      paperRect(960, 1010, 2060, 200, '#8a6a47', { seed: 410, torn: true, depth: 1.2, boil: false });
    }, { zoom: 0.75 });
  }
  /* s4: the town square — sky, rooftops, a paper crowd */
  function bgStreet() {
    layer('fs_street', 0, 0, 1920, 1080, () => {
      const C = KC();
      g.fillStyle = paperTile('#e9e1cf', 5); g.fillRect(0, 0, 1920, 1080);
      [[120, 420, 300, '#c9b28f'], [420, 360, 260, '#b9a07c'], [1500, 380, 300, '#c9b28f'], [1800, 430, 280, '#b9a07c'], [960, 470, 420, '#d4c19e']].forEach(([x, y, w, col], i) => {
        paperRect(x, y + 300, w, 600, col, { seed: 420 + i, depth: 1, boil: false });
        for (let r = 0; r < 4; r++) for (let k = -1; k <= 1; k++) paperRect(x + k * w * 0.28, y + 60 + r * 110, 50, 64, '#6e6254', { seed: 430 + i * 12 + r * 3 + k, depth: 0, boil: false });
      });
      paperRect(960, 960, 2060, 260, '#a39a8a', { seed: 480, torn: true, depth: 1.2, boil: false });
      const cols = ['#7a6f63', '#6b5f53', '#857868'], r = rng(481);
      for (let i = 0; i < 26; i++) Civic.icon('people', 60 + i * 72 + r() * 20, 880 + (i % 3) * 18, 110, { seed: 482 + (i % 6), color: cols[i % 3] });
    }, { zoom: 0.75 });
  }
  function bgParch() {
    layer('fs_parch', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile(KC().parchment, 7); g.fillRect(0, 0, 1920, 1080);
      texScrap('tex_news', 160, 150, 360, 260, { rot: -0.08, seed: 360, depth: 0.6, tint: KC().parchment, tintAlpha: 0.6 });
      texScrap('tex_news', 1780, 980, 380, 240, { rot: 0.06, seed: 361, depth: 0.6, tint: KC().parchment, tintAlpha: 0.6 });
      Civic.bunting(-20, 980, 4, { sag: 40, n: 11, seed: 8, sway: 0 }); Civic.bunting(940, 1940, 4, { sag: 40, n: 11, seed: 9, sway: 0 });
    }, { zoom: 0.75 });
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, color = RED, w = 9) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  function check(x, y, s, prog, seed = 1, color = KC().navy) { ink([[x - 34 * s, y - 2 * s], [x - 8 * s, y + 26 * s], [x + 40 * s, y - 38 * s]], { w: 11 * s, color, seed, prog, passes: 2, step: 8 }); }
  function cross(x, y, s, prog, seed = 1, color = RED) {
    ink([[x - 26 * s, y - 26 * s], [x + 26 * s, y + 26 * s]], { w: 10 * s, color, seed, prog: Math.min(1, prog * 2), passes: 2, step: 8 });
    ink([[x + 26 * s, y - 26 * s], [x - 26 * s, y + 26 * s]], { w: 10 * s, color, seed: seed + 1, prog: Math.max(0, prog * 2 - 1), passes: 2, step: 8 });
  }
  function tag(str, x, y, size, s = 1, o = {}) {
    if (s <= 0.001) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.04); g.scale(s, s);
    const font = `${size}px ${o.font || HANDF}`, w = Math.round(tw(str, font) + size * 0.9), h = Math.round(size * 1.4);
    paperRect(0, 0, w, h, o.color || KC().cream, { seed: o.seed || 41, depth: o.depth ?? 1.0, outline: true });
    g.font = font; g.fillStyle = o.ink || INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, size * 0.05);
    g.restore();
  }
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 280, size = o.size || 34, seed = o.seed || 610, h = Math.round(44 + lines.length * size * 1.15);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || '#f2d479', { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, -h / 2 + 22 + size * 0.62 + i * size * 1.15, size, (o.times && o.times[i] != null ? o.times[i] : t0 + 0.1 + i * 0.12), { color: o.ink || INK, seed: seed + i + 1, dur: 0.3 }));
    if (o.strike != null) strike(-w / 2 + 16, h / 2 - 18, w / 2 - 16, -h / 2 + 18, P(T, o.strike, o.strike + 0.35), seed + 9);
    tape(0, -h / 2 + 2, 96, 0.05, undefined, seed + 20);
    g.restore();
  }
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px ${TYPE}`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));
  /* a parchment scroll unrolled to u (0..1) from its top; content() draws inside it (clipped) */
  function scroll(x, y, w, h, u, content, seed = 11) {
    if (u <= 0.001) return;
    const top = y - h / 2, vis = h * u;
    g.save(); g.beginPath(); g.rect(x - w / 2 - 40, top - 30, w + 80, vis + 30); g.clip();
    Civic.parchment(x, y, w, h, { seed, depth: 1.3 });
    if (content) content();
    g.restore();
    paperRect(x, top, w + 44, 30, '#d8c08e', { seed: seed + 1, depth: 0.9, outline: true, boil: false });
    paperRect(x, top + vis, w + 44, 30, '#d8c08e', { seed: seed + 2, depth: 0.9, outline: true, boil: false });
  }
  /* a column board: a coloured header, rows of hand-written items (each [text, t0]); returns the rows' y */
  function board(x, top, W, H, t0, head, color, inkC, items, o = {}) {
    const s = pop(T, t0, 0.4, 1.8), ys = [];
    const rowY = i => top + (o.first || 130) + i * (o.pitch || 84);
    items.forEach((_, i) => ys.push(rowY(i)));
    if (s <= 0) return ys;
    g.save(); g.translate(x, top + H / 2); g.rotate(o.rot || 0); g.scale(s, s); g.translate(-x, -(top + H / 2));
    paperRect(x, top + H / 2, W, H, KC().cream, { seed: o.seed || 500, depth: 1.3 });
    paperRect(x, top + 40, W - 8, 70, color, { seed: (o.seed || 500) + 1, depth: 0.2 });
    txt(head, x, top + 42, fitSize(head, W - 40, 38, SLAB), { font: SLAB, color: inkC });
    items.forEach(([str, te], i) => {
      if (T < te) return;
      const size = fitSize(str, W - (o.pad || 90), 42);
      hand(str, x - W / 2 + (o.indent || 44), rowY(i), size, te, { align: 'left', color: INK, seed: (o.seed || 500) + 10 + i, dur: 0.35 });
    });
    g.restore();
    return ys;
  }

  /* ======================================================================
     Staging: puppets, captions, chapter cards (as lessons/branches)
     ====================================================================== */
  const POS = {
    two: { rex: { x: 250, y: 1088, h: 540, dip: 0 }, judge: { x: 1665, y: 1088, h: 610, dip: 0 } },
    inset: { rex: { x: 124, y: 1098, h: 300, dip: 56 }, judge: { x: 1796, y: 1098, h: 330, dip: 56 } },
  };
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  function stage(t, part, keys) {
    const out = {};
    ['rex', 'judge'].forEach(who => {
      const v = Object.assign({}, POS[keys[0][1]][who]);
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + 0.7)), p = POS[k[1]][who];
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  }
  const REX_POINT = new Set(['s1_04', 's2_01', 's3_02', 's4_01', 's4_03', 's5_06']);
  const JUDGE_POINT = new Set(['s1_03', 's1_05', 's1_07', 's2_04', 's3_03', 's3_05', 's4_02', 's4_04', 's5_02', 's5_03', 's5_05']);
  function cast(t, ctx, st, o = {}) {
    const cap = ctx.caption, id = cap ? cap.id : '', who = cap ? cap.who : '';
    const rp = who === 'rex' && REX_POINT.has(id) ? 'point' : null;
    const jp = who === 'judge' && (JUDGE_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('rex', st.rex.x, st.rex.y, st.rex.h, { env: ctx.speaking('rex'), facing: 1, t, pose: rp });
    if (o.front) o.front();
    Puppet.draw('judge', st.judge.x, st.judge.y, st.judge.h, { env: ctx.speaking('judge'), facing: -1, t, pose: jp });
    bubble(ctx, st);
  }
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  function bubble(ctx, st) {
    const l = ctx.caption; if (!l) return;
    const p = st[l.who]; if (!p) return;
    const rex = l.who === 'rex', dir = rex ? 1 : -1, side = rex ? 'left' : 'right', k = BS() / 40;
    const tall = p.h > 420;
    const f = fit(l, Math.round(700 * k), Math.round(860 * k));
    const w = f.w, bh = f.h;
    let cx, cy;
    if (tall) { cx = rex ? p.x - 60 + w / 2 : p.x + 60 - w / 2; cy = 84 + bh / 2; }
    else { cx = p.x + dir * (0.2 * p.h + w / 2 + 16); cy = Math.min(876, 968 - bh / 2); }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    const cap = { x: cx, y: cy, side, w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  /* ---------- chapter cards: the card holds, then tears away to the scene ---------- */
  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  const cardW = new Map();
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, C = KC();
    g.fillStyle = paperTile(C.kraft, 23, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    paperRect(960, 548, 1780, 960, C.parchment, { seed: 900 + n, depth: 1.6, boil: false });
    Civic.bunting(140, 1780, 76, { sag: 40, n: 13, seed: 5 + n, sway: 0 });
    txt('CHAPTER', 960, 300, 52, { font: TYPE, color: C.navy });
    Civic.seal(960, 410, 80, { text: String(n), seed: 910 + n });
    const title = String(seg.chapter || seg.id).toUpperCase();
    let w0 = cardW.get(title);
    if (w0 == null) { w0 = ransom(title, -5000, -5000, 100, 1e9, { seed: 950 + n, palette: TITLE_PAL }); if (fontOk()) cardW.set(title, w0); }
    const size = Math.min(124, (1480 / Math.max(1, w0)) * 100);
    ransom(title, 960, 680, size, -1e6, { seed: 950 + n, palette: TITLE_PAL });
    const sub = '~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~';
    hand(sub, 960, 880, fitSize(sub, 1500, 44), -1e6, { dur: 0.01, color: 'rgba(31,47,85,0.7)', seed: 960 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  /* ======================================================================
     the five scenes (BODY[id](t, part, ctx, c) draws the stage; S[id] wraps it with the chapter card)
     ====================================================================== */
  const BODY = {};

  /* ---- s1 "Deleted": the deleted post, the First Amendment, the five freedoms, GOVERNMENT vs PRIVATE ---- */
  const S1 = { x: 1000, y: 525, w: 720, h: 650, badge: 96 };
  function phone(x, y, s, rot, t, tm) {
    if (s <= 0.001) return;
    const C = KC();
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, 250, 440, '#26221f', { seed: 101, depth: 1.4 });
    paperRect(0, 4, 214, 368, '#fffdf7', { seed: 102, depth: 0, boil: false });
    paperRect(0, -160, 214, 44, C.brick, { seed: 103, depth: 0, boil: false });
    txt('clipz', -88, -160, 26, { font: SLAB, color: C.cream, align: 'left' });
    paperCircle(-70, -96, 26, C.navy, { seed: 104, depth: 0.3 });
    txt('@rex.d', -34, -96, 24, { font: TYPE, color: INK, align: 'left' });
    ['HOODIES', 'ARE A', 'HUMAN', 'RIGHT'].forEach((l, i) => hand(l, 0, -28 + i * 44, 40, tm.post + i * 0.12, { font: SLAB, color: C.navy, seed: 105 + i, dur: 0.25 }));
    const sd = stampS(t, tm.del);
    if (sd > 0) { stampMark('REMOVED', 0, 30, sd * 0.85, { rot: -0.42, size: 54 }); if (t > tm.del + 0.2) txt('by the app', 0, 150, 28, { color: RED, rot: -0.05 }); }
    g.restore();
  }
  function amendmentScroll(t, tm) {
    const C = KC(), top = S1.y - S1.h / 2, L = S1.x - S1.w / 2 + 50, maxW = S1.w - 100;
    Civic.woodtype('FIRST AMENDMENT', S1.x, top + 62, 38, { color: C.navy, ink: C.cream, t0: tm.un + 0.1, seed: 14 });
    hand(HEADLINE, S1.x, top + 142, fitSize(HEADLINE, maxW, 58, SERIF), tm.un + 0.4, { font: SERIF, color: INK, seed: 15, dur: 0.6 });
    if (t > tm.cong) ink([[S1.x - 290, top + 178], [S1.x + 290, top + 176]], { w: 6, color: RED, seed: 16, prog: P(t, tm.cong, tm.cong + 0.4), passes: 2, step: 12 });
    const rest = amend1().slice(HEADLINE.length).trim();
    const lines = wrapLines(rest, maxW, 28, TYPE);
    lines.forEach((l, i) => hand(l, L, top + 222 + i * 37, 28, tm.un + 0.8 + i * 0.25, { font: TYPE, align: 'left', color: INK, seed: 20 + i, dur: 0.4 }));
    // the five freedoms, popping in as the Judge names them
    const by = top + S1.h - 128;
    Civic.FREEDOMS.forEach((id, k) => {
      const x = S1.x + (k - 2) * 132, s = pop(t, tm.fr[k], 0.35, 2.4);
      Civic.freedom(id, x, by, S1.badge, { s, seed: 40 + k * 7, label: true, t0: tm.fr[k] + 0.1, rot: (k - 2) * 0.03 });
    });
    if (t > tm.two) inkCircle(S1.x - 132, by + 24, 72, { sy: 1.3, w: 6, color: RED, seed: 47, prog: P(t, tm.two, tm.two + 0.4) });
    stampMark('GOVERNMENT', S1.x, S1.y + 40, stampS(t, tm.gov) * 0.95, { rot: -0.16, size: 88 });
  }
  BODY.s1 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q);
    bgShop();
    Civic.bunting(-20, 980, 4, { sag: 46, n: 11, seed: 2 }); Civic.bunting(940, 1940, 4, { sag: 46, n: 11, seed: 3 });
    const tm = {
      post: c.W('s1_01', 'post'), del: c.W('s1_01', 'deleted'), read: c.W('s1_02', 'read'), un: c.W('s1_03', 'First'),
      fr: [c.W('s1_03', 'Religion'), c.W('s1_03', 'speech'), c.W('s1_03', 'press'), c.W('s1_03', 'assembly'), c.W('s1_03', 'petitioning')],
      two: c.W('s1_04', 'two'), cong: c.W('s1_05', 'Congress'), gov: c.W('s1_05', 'government'),
      shrink: rs - 0.1, gcol: branch(c, every(q, 'government')), pcol: branch(c, every(q, 'private')), slide: branch(c, every(q, 'company')),
      store: c.W('s1_07', 'store'), pschool: c.W('s1_07', 'private'), parents: c.W('s1_07', 'parents'), pub: c.W('s1_07', 'public'), isgov: c.W('s1_07', 'government'),
    };
    // the rolled-up Bill of Rights (s1_02 "Let's read the thing"), replaced by the unrolling parchment (s1_03)
    if (t > tm.read && t < tm.un + 0.15) Civic.prop('scroll_rights', S1.x, S1.y, 340, { s: pop(t, tm.read, 0.35) * (1 - P(t, tm.un - 0.1, tm.un + 0.15)), rot: -0.25, seed: 60 });
    // the First Amendment: unrolls (s1_03), five freedoms, GOVERNMENT stamp (s1_05); shrinks to the top in the reply
    const e = E.inOutCubic(P(t, tm.shrink, tm.shrink + 0.8));
    const un = E.outCubic(P(t, tm.un, tm.un + 0.7));
    if (un > 0) at(lerp(S1.x, 960, e), lerp(S1.y, 150, e), lerp(1, 0.2, e), S1.x, S1.y, () => scroll(S1.x, S1.y, S1.w, S1.h, un, () => amendmentScroll(t, tm), 70));
    if (t > tm.un + 0.5 && e < 0.5) dock('amend1', ['The First Amendment (1791):', `"${amend1()}"`]);
    // the two columns (the reply): GOVERNMENT vs PRIVATE
    const GX = 620, PX = 1300, TOP = 270, W = 560, H = 500;
    const gy = board(GX, TOP, W, H, tm.gcol, 'GOVERNMENT', C.navy, C.cream,
      [['Congress', tm.gcol + 0.3], ['a public school', tm.gcol + 0.55], ['the police', tm.gcol + 0.8]], { seed: 510, rot: -0.01, first: 150, pitch: 100, indent: 140 });
    if (t > tm.gcol + 0.2) Civic.building('capitol', GX - W / 2 + 80, gy[0] + 34, 70, { seed: 520, s: pop(t, tm.gcol + 0.3, 0.3) });
    const py = board(PX, TOP, W, H, tm.pcol, 'PRIVATE', C.kraft, INK,
      [['a video app', tm.pcol + 0.3], ['a store', tm.pcol + 0.55], ['a private school', tm.pcol + 0.8], ['your parents', tm.pcol + 1.05]], { seed: 530, rot: 0.012, first: 130, pitch: 96, indent: 50 });
    // s1_07: a store, a private school, your parents ✓ (private); a public school = the government
    [[tm.store, 1], [tm.pschool, 2], [tm.parents, 3]].forEach(([t0, i], k) => { if (t > t0) check(PX + W / 2 - 60, py[i], 0.8, P(t, t0, t0 + 0.3), 540 + k, GREEN); });
    if (t > tm.pub) inkCircle(GX - W / 2 + 140 + tw('a public school', `42px ${HANDF}`) / 2, gy[1], 140, { sx: 1.2, sy: 0.42, w: 6, color: RED, seed: 545, prog: P(t, tm.pub, tm.pub + 0.45) });
    if (t > tm.isgov) tag('= the government', GX + 140, gy[1] + 58, 32, pop(t, tm.isgov, 0.35), { color: '#f2d479', ink: C.navy, seed: 546, rot: -0.05 });
    // Rex's phone: in his hand (s1_01), aside while the scroll is open, then into PRIVATE ("a private company")
    const ph = track(t, [[0, 640, 560, 1], [tm.un - 0.4, 480, 800, 0.5], [tm.slide, PX + W / 2 - 80, py[0] + 6, 0.3, 0.8]]);
    const rot = lerp(-0.08, 0.1, P(t, tm.un - 0.4, tm.un + 0.2)) - 0.1 * P(t, tm.slide, tm.slide + 0.8);
    phone(ph.x, ph.y, ph.s * pop(t, CARD.hold - 0.3, 0.3), rot, t, tm);
    const st = stage(t, part, [[0, 'two'], [tm.shrink, 'inset']]);
    cast(t, ctx, st);
  };
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.2, reply: 1.4, after: 1.8 }, gaps: { s1_03: 0.6, s1_05: 0.5 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'pop', t: c.W('s1_01', 'post'), gain: 0.25 }, { name: 'stamp', t: c.W('s1_01', 'deleted'), gain: 0.55 });
      out.push({ name: 'pop', t: c.W('s1_02', 'read'), gain: 0.3 }, { name: fx('unroll', 'paper'), t: c.W('s1_03', 'First'), gain: 0.5 });
      ['Religion', 'speech', 'press', 'assembly', 'petitioning'].forEach(w => out.push({ name: 'pop', t: c.W('s1_03', w), gain: 0.25 }));
      out.push({ name: 'scribble', t: c.W('s1_04', 'two'), gain: 0.25 }, { name: 'scribble', t: c.W('s1_05', 'Congress'), gain: 0.25 });
      out.push({ name: 'press', t: c.W('s1_05', 'government') - 0.05, gain: 0.5 }, { name: 'stamp', t: c.W('s1_05', 'government'), gain: 0.55 });
      out.push({ name: 'whoosh', t: replyStart(c, q) - 0.1, gain: 0.25 });
      out.push({ name: 'pop', t: branch(c, every(q, 'government')), gain: 0.3 }, { name: 'pop', t: branch(c, every(q, 'private')), gain: 0.3 });
      out.push({ name: 'whoosh', t: branch(c, every(q, 'company')), gain: 0.3 });
      ['store', 'private', 'parents'].forEach(w => out.push({ name: 'scribble', t: c.W('s1_07', w), gain: 0.18 }));
      out.push({ name: 'scribble', t: c.W('s1_07', 'public'), gain: 0.25 }, { name: 'pop', t: c.W('s1_07', 'government'), gain: 0.3 });
      return out;
    },
  };

  /* ---- s2 "The schoolhouse gate": the NO HOODIES notice, the armband, Tinker, the quote, the balance ---- */
  const S2 = { dk: { x: 600, y: 330, w: 820, h: 400 }, qc: { x: 1340, y: 340, w: 680, h: 440 }, bal: { x: 1080, y: 250, arm: 330, drop: 190 } };
  function quoteCard(t, t0) {
    const C = KC(), Q = S2.qc, s = pop(t, t0, 0.4, 1.6);
    if (s <= 0) return;
    g.save(); g.translate(Q.x, Q.y); g.rotate(0.012); g.scale(s, s);
    paperRect(0, 0, Q.w, Q.h, '#fbf3df', { seed: 610, depth: 1.5 });
    paperRect(-Q.w / 2 + 14, 0, 14, Q.h - 20, C.brick, { seed: 611, depth: 0 });
    tape(0, -Q.h / 2 + 2, 130, -0.04, undefined, 612);
    g.restore();
    if (s < 0.95) return;
    const L = Q.x - Q.w / 2 + 50, top = Q.y - Q.h / 2;
    Civic.woodtype('SCHOOLHOUSE GATE', Q.x + 6, top + 56, 34, { color: C.navy, ink: C.cream, t0: t0 + 0.1, seed: 613, stagger: 0.03 });
    const tk = CASE('tinker');
    txt(`${tk.name}, ${tk.year} · the Court:`, L, top + 118, 24, { font: TYPE, color: PENCIL, align: 'left' });
    const lines = wrapLines('"' + TEXT.tinkerQuote + '"', Q.w - 90, 38);
    lines.forEach((l, i) => hand(l, L, top + 170 + i * 48, 38, t0 + 0.3 + i * 0.35, { align: 'left', color: INK, seed: 614 + i, dur: 0.45 }));
  }
  function balance(t, tm, s) {
    if (s <= 0.001) return;
    const C = KC(), B = S2.bal;
    // tilt: level → the silent protest's side down (protected) → the disruption side down ("If it had", s2_06)
    const a = -0.1 * E.inOutCubic(P(t, tm.silentR, tm.silentR + 0.6)) + 0.24 * E.inOutCubic(P(t, tm.had, tm.had + 0.7));
    g.save(); g.translate(B.x, B.y); g.scale(s, s);
    paperRect(0, 250, 30, 500, '#6e5238', { seed: 700, depth: 1 });
    paperRect(0, 500, 220, 36, '#6e5238', { seed: 701, depth: 1 });
    paperPiece(Object.assign([[-22, 0], [22, 0], [0, -40]], { k: 'fs:fulcrum' }), 0, 6, C.gold, { seed: 702, depth: 0.6 });
    g.save(); g.rotate(a); paperRect(0, 0, B.arm * 2 + 40, 20, C.gold, { seed: 703, depth: 0.8 }); g.restore();
    [-1, 1].forEach(side => {
      const ex = side * B.arm * Math.cos(a), ey = side * B.arm * Math.sin(a), py = ey + B.drop;
      ink([[ex, ey], [ex - 110, py]], { w: 3, color: INK, seed: 704 + side, passes: 1, step: 40 });
      ink([[ex, ey], [ex + 110, py]], { w: 3, color: INK, seed: 706 + side, passes: 1, step: 40 });
      paperPiece(Object.assign([[-140, 0], [140, 0], [100, 34], [-100, 34]], { k: 'fs:pan' }), ex, py, '#8a6a47', { seed: 708 + side, depth: 1 });
      if (side < 0) {
        Civic.prop('armband', ex, py - 34, 90, { seed: 712 });
        txt('SILENT PROTEST', ex, py + 76, 30, { font: SLAB, color: C.navy });
      } else {
        for (let k = 0; k < 3; k++) ink([[ex - 70 + k * 20, py - 20], [ex - 30 + k * 30, py - 70], [ex + 10 + k * 20, py - 18], [ex + 40 + k * 10, py - 64]], { w: 4, color: RED, seed: 713 + k, passes: 1, step: 10 });
        txt('SUBSTANTIAL', ex, py + 70, 26, { font: SLAB, color: RED });
        txt('DISRUPTION', ex, py + 102, 26, { font: SLAB, color: RED });
      }
    });
    g.restore();
    const lx = B.x - B.arm * s, rx = B.x + B.arm * s, yb = B.y + (B.drop + 170) * s;
    if (t > tm.silentR) tag('protected ✓', lx, yb, 34, pop(t, tm.silentR + 0.3, 0.35), { color: '#fffdf7', ink: GREEN, seed: 716, rot: -0.04 });
    if (t > tm.disrupt) tag('unless…', rx, yb + 10, 34, pop(t, tm.disrupt, 0.35), { color: '#fffdf7', ink: RED, seed: 717, rot: 0.04 });
    if (t > tm.step) tag('the school can step in', rx - 40, yb + 76, 32, pop(t, tm.step, 0.35), { color: '#f2d479', ink: INK, seed: 718, rot: -0.03 });
  }
  BODY.s2 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q), tk = CASE('tinker');
    bgGate();
    const tm = {
      rule: CARD.hold + 0.4, arm: c.W('s2_01', 'armband'), ins: c.L('s2_02') - 0.3, year: c.W('s2_02', 'nineteen'),
      susp: c.W('s2_03', 'suspended'), thirteen: c.W('s2_03', 'thirteen'), sc: c.W('s2_03', 'Supreme'), wrote: c.W('s2_04', 'wrote'),
      rs, silentR: branch(c, every(q, 'silent')), disrupt: branch(c, every(q, 'substantially')),
      seven: c.W('s2_05', 'Seven'), had: c.W('s2_06', 'had'), step: c.W('s2_06', 'step'),
    };
    // the notice on the gate
    const ns = pop(t, tm.rule, 0.4) * (1 - P(t, rs - 0.1, rs + 0.3));
    if (ns > 0) {
      g.save(); g.translate(960, 420); g.rotate(-0.03); g.scale(ns, ns);
      paperRect(0, 0, 420, 250, '#fffdf7', { seed: 720, depth: 1.3 });
      txt('NOTICE', 0, -80, 34, { font: TYPE, color: C.brick });
      txt('NEW RULE:', 0, -20, 48, { font: SLAB, color: INK });
      txt('NO HOODIES', 0, 50, 56, { font: SLAB, color: RED });
      tape(0, -125, 110, 0.04, undefined, 721);
      g.restore();
    }
    const fade = 1 - P(t, rs - 0.1, rs + 0.4);
    // 1965 · Des Moines (s2_02), suspended (s2_03), Mary Beth Tinker's armband with "13" (s2_03)
    alpha(fade, () => {
      if (t > tm.year) tag(`DES MOINES, IOWA · ${TEXT.armbandYear}`, 500, 590, 34, pop(t, tm.year, 0.35), { font: SLAB, color: C.navy, ink: C.cream, seed: 722, rot: -0.04 });
      stampMark('SUSPENDED', 560, 664, stampS(t, tm.susp) * 0.55, { rot: 0.06 });
      if (t > tm.thirteen) {
        const s = pop(t, tm.thirteen, 0.35);
        Civic.prop('armband', 930, 596, 150, { s, rot: -0.12, seed: 723 });
        tag(String(TEXT.maryBethAge), 1010, 640, 40, s, { font: SLAB, color: '#fffdf7', ink: INK, seed: 724, rot: 0.12 });
      }
      quoteCard(t, tm.wrote);
    });
    // the docket: slides in (s2_03 "Supreme Court"), shrinks to the corner in the reply
    const d = S2.dk, dp = track(t, [[0, 2500, d.y, 1], [tm.sc, d.x, d.y, 1, 0.5], [rs - 0.1, 330, 190, 0.48, 0.8]]);
    if (t > tm.sc) docketAt(dp.x, dp.y, dp.s, d.w, d.h, tk, { rot: -0.02, seed: 730, t0: tm.sc + 0.4,
      lines: [`Des Moines, Iowa, ${TEXT.armbandYear}:`, 'students wear black armbands', 'to school to protest a war.', 'The schools suspend them.'] });
    if (t > tm.seven) {
      const r = Math.min(d.h * 0.16, d.w * 0.11), sx = dp.x + (d.w / 2 - r * 1.3) * dp.s, sy = dp.y + (-d.h / 2 + r * 1.35) * dp.s;
      inkCircle(sx, sy, r * dp.s + 22, { w: 6, color: RED, seed: 731, prog: P(t, tm.seven, tm.seven + 0.4) });
      tag(`${VOTE(tk.vote)} for the students`, sx + 40, sy + 110, 32, pop(t, tm.seven + 0.3, 0.35), { color: '#f2d479', ink: INK, seed: 732, rot: 0.04 });
    }
    if (t > tm.sc && fade > 0.5) dock('tinker', [`${tk.name}, decided ${tk.year}, ${VOTE(tk.vote)}.`].concat(t > tm.wrote ? [`"${TEXT.tinkerQuote}"`] : []));
    // the balance (the reply and after)
    balance(t, tm, pop(t, rs + 0.2, 0.45, 1.6));
    // Rex's armband (s2_01) rides on his sleeve
    const st = stage(t, part, [[0, 'two'], [tm.ins, 'inset']]);
    cast(t, ctx, st, { front: () => { if (t > tm.arm) Civic.prop('armband', st.rex.x + st.rex.h * 0.14, st.rex.y - st.rex.h * 0.64, st.rex.h * 0.11, { s: pop(t, tm.arm, 0.35), rot: 0.15, seed: 740 }); } });
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.6, reply: 1.4, after: 1.8 }, gaps: { s2_02: 0.5, s2_04: 0.6 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'paper', t: CARD.hold + 0.4, gain: 0.35 }, { name: 'pop', t: c.W('s2_01', 'armband'), gain: 0.35 });
      out.push({ name: 'pop', t: c.W('s2_02', 'nineteen'), gain: 0.3 }, { name: 'stamp', t: c.W('s2_03', 'suspended'), gain: 0.45 });
      out.push({ name: 'pop', t: c.W('s2_03', 'thirteen'), gain: 0.3 }, { name: 'whoosh', t: c.W('s2_03', 'Supreme'), gain: 0.3 }, { name: 'stamp', t: c.W('s2_03', 'Supreme') + 0.4, gain: 0.35 });
      out.push({ name: 'paper', t: c.W('s2_04', 'wrote'), gain: 0.35 }, { name: 'scribble', t: c.W('s2_04', 'wrote') + 0.3, gain: 0.2 });
      out.push({ name: 'thunk', t: replyStart(c, q) + 0.25, gain: 0.35 }, { name: 'ding', t: branch(c, every(q, 'silent')) + 0.3, gain: 0.3 }, { name: 'pop', t: branch(c, every(q, 'substantially')), gain: 0.3 });
      out.push({ name: 'scribble', t: c.W('s2_05', 'Seven'), gain: 0.3 }, { name: 'thunk', t: c.W('s2_06', 'had'), gain: 0.35 }, { name: 'pop', t: c.W('s2_06', 'step'), gain: 0.3 });
      return out;
    },
  };

  /* ---- s3 "Stop the presses": the Courier, the red CUT, Hazelwood, school paper vs home zine ---- */
  const S3 = { x: 960, y: 560, w: 620, h: 700 };
  function courier(x, y, s, t, tm) {
    if (s <= 0.001) return;
    const C = KC(), { w, h } = S3, top = -h / 2;
    g.save(); g.translate(x, y); g.rotate(-0.012); g.scale(s, s);
    paperRect(0, 0, w, h, PAL.white, { seed: 800, depth: 1.4, tex: 'tex_news' });
    Civic.masthead('The Coral Ridge Courier', 0, top + 78, w - 60, { size: 40, date: 'made in Journalism class', price: 'Vol. 3' });
    ['THE CAFETERIA MENU:', 'A CRIME STORY'].forEach((l, i) => txt(l, 0, top + 196 + i * 42, fitSize(l, w - 80, 34, SLAB), { font: SLAB, color: INK }));
    txt('by Rex Delgado', 0, top + 282, 22, { font: TYPE, color: PENCIL });
    for (let k = 0; k < 9; k++) for (const cx of [-1, 1]) ink([[cx * 145 - 120, top + 330 + k * 34], [cx * 145 + 120, top + 330 + k * 34]], { w: 2.2, color: 'rgba(43,36,32,0.45)', seed: 801 + k * 2 + (cx > 0), passes: 1, step: 40, amp: 0.4 });
    // the red CUT (s3_01)
    if (t > tm.cut) {
      const p = P(t, tm.cut, tm.cut + 0.5);
      cross(0, top + 440, 5.2, p, 820);
      stampMark('CUT', 170, top + 560, stampS(t, tm.cut + 0.35) * 0.9, { size: 80, rot: 0.12 });
    }
    stampMark('SCHOOL-SPONSORED', 0, top + 640, stampS(t, tm.rSchool) * 0.62, { size: 60, rot: -0.05, color: KC().navy });
    g.restore();
  }
  function zine(x, y, s, t0) {
    if (s <= 0.001) return;
    const C = KC(), w = 500, h = 640, top = -h / 2;
    g.save(); g.translate(x, y); g.rotate(0.02); g.scale(s, s);
    paperRect(0, 0, w, h, '#f6efe0', { seed: 850, depth: 1.4 });
    Civic.woodtype('REX REPORTS', 0, top + 70, 40, { color: C.brick, ink: C.cream, t0, seed: 851 });
    txt('printed at home · free', 0, top + 132, 24, { font: TYPE, color: PENCIL });
    ['THE CAFETERIA MENU:', 'A CRIME STORY', '(uncut)'].forEach((l, i) => hand(l, 0, top + 200 + i * 44, i < 2 ? 34 : 30, t0 + 0.3 + i * 0.15, { font: i < 2 ? SLAB : HANDF, color: i < 2 ? INK : RED, seed: 852 + i, dur: 0.3 }));
    for (let k = 0; k < 8; k++) ink([[-200, top + 350 + k * 32], [200, top + 350 + k * 32]], { w: 2.2, color: 'rgba(43,36,32,0.4)', seed: 856 + k, passes: 1, step: 40, amp: 0.6 });
    g.restore();
  }
  BODY.s3 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q), hz = CASE('hazelwood');
    bgNews();
    const tm = {
      art: c.W('s3_01', 'article'), cut: c.W('s3_01', 'cut'), press: c.W('s3_02', 'Freedom'), pub: c.W('s3_03', 'publishes'),
      school: c.W('s3_04', 'school'), cls: c.W('s3_04', 'class'), grades: c.W('s3_04', 'grades'),
      rs, rSchool: branch(c, every(q, 'school')), teach: branch(c, every(q, 'teaching')),
      hz: c.W('s3_05', 'Hazelwood'), split: c.L('s3_06') - 0.3, home: c.W('s3_06', 'home'), money: c.W('s3_06', 'money'),
      yours: c.W('s3_07', 'yours'), diff: c.W('s3_07', 'Different'),
    };
    // the Courier: centre (lines + reply), left with Hazelwood beside it, then the left half of the comparison
    const cp = track(t, [[0, S3.x, S3.y, 1], [tm.hz - 0.3, 560, 450, 0.72, 0.7], [tm.split, 600, 470, 0.66, 0.7]]);
    courier(cp.x, cp.y, cp.s * pop(t, CARD.hold - 0.2, 0.35), t, tm);
    // the principal's red pen strikes on "cut"
    if (t > tm.cut - 0.4 && t < tm.cut + 1.4) Civic.tool('pen', S3.x + 260, S3.y - 40 + 60 * E.inOutSine(P(t, tm.cut - 0.3, tm.cut + 0.4)), 160, { rot: 0.7, seed: 860, s: pop(t, tm.cut - 0.4, 0.25) * (1 - P(t, tm.cut + 1.0, tm.cut + 1.4)) });
    // "Freedom of the press!" (s3_02): the press badge with a question mark
    alpha(1 - P(t, rs - 0.2, rs + 0.3), () => {
      if (t > tm.press) { Civic.freedom('press', 520, 340, 130, { s: pop(t, tm.press, 0.35), seed: 861, label: true, t0: tm.press + 0.1 }); }
      // "Who publishes that paper?" (s3_03) — answered by Rex (s3_04)
      if (t > tm.pub) sticky(['Who publishes it?', 'the school ✓', 'a class ✓', 'graded ✓'], 1420, 330, tm.pub, { w: 300, size: 34, seed: 862, times: [tm.pub + 0.1, tm.school, tm.cls, tm.grades], rot: 0.05 });
    });
    // the reply: SCHOOL-SPONSORED (stamped in courier()), "can edit for teaching reasons"
    if (t > tm.teach) alpha(1 - P(t, tm.hz - 0.4, tm.hz), () => tag('can edit it for teaching reasons', S3.x, S3.y + S3.h / 2 + 20, 36, pop(t, tm.teach, 0.35), { color: '#f2d479', ink: INK, seed: 863, rot: -0.02 }));
    // after: Hazelwood v. Kuhlmeier (s3_05), gone at the comparison (s3_06)
    const dx = lerp(2400, 1330, E.outCubic(P(t, tm.hz, tm.hz + 0.5))), gone = E.inOutCubic(P(t, tm.split, tm.split + 0.6));
    if (t > tm.hz && gone < 1) alpha(1 - gone, () => docketAt(dx + gone * 700, 390, 1, 780, 440, hz, { rot: 0.015, seed: 870, t0: tm.hz + 0.4,
      lines: ['The principal cut pages from', 'a school-sponsored paper.', 'A paper made in class', 'is part of school.'] }));
    if (t > tm.hz && gone < 0.5) dock('hazelwood', [`${hz.name}, decided ${hz.year}, ${VOTE(hz.vote)}.`, 'A school-sponsored paper can be edited for teaching reasons.']);
    // after: SCHOOL-SPONSORED vs HOME-PRINTED (s3_06), the rules under each (s3_07)
    if (t > tm.split) {
      const s = pop(t, tm.split + 0.3, 0.35);
      tag('SCHOOL-SPONSORED', 600, 120, 38, s, { font: SLAB, color: C.navy, ink: C.cream, seed: 871, rot: -0.02 });
      if (t > tm.home) tag('HOME-PRINTED', 1290, 120, 38, pop(t, tm.home, 0.35), { font: SLAB, color: C.brick, ink: C.cream, seed: 872, rot: 0.02 });
      zine(1290, 470, 0.66 * pop(t, tm.home, 0.4, 1.6), tm.home + 0.2);
      if (t > tm.money) Civic.prop('printing_press', 1600, 640, 230, { s: pop(t, tm.money, 0.4), seed: 873 });
      if (t > tm.yours) tag("yours: your rules", 1290, 760, 36, pop(t, tm.yours, 0.35), { color: '#fffdf7', ink: C.brick, seed: 874, rot: 0.03 });
      if (t > tm.diff) tag("the school's paper: school rules", 600, 760, 34, pop(t, tm.diff, 0.35), { color: '#fffdf7', ink: C.navy, seed: 875, rot: -0.03 });
    }
    const st = stage(t, part, [[0, 'two'], [tm.hz - 0.4, 'inset']]);
    cast(t, ctx, st, { front: () => { if (t < tm.pub) Civic.prop('newspaper', st.rex.x + st.rex.h * 0.3, st.rex.y - st.rex.h * 0.45, st.rex.h * 0.36, { s: pop(t, tm.art, 0.35) * (1 - P(t, tm.pub - 0.3, tm.pub)), rot: 0.2, seed: 876 }); } });
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.6, after: 2.0 }, gaps: { s3_02: 0.5, s3_06: 0.6 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: 'paper', t: CARD.hold, gain: 0.3 }, { name: 'pop', t: c.W('s3_01', 'article'), gain: 0.25 });
      out.push({ name: 'scribble', t: c.W('s3_01', 'cut'), gain: 0.45 }, { name: 'stamp', t: c.W('s3_01', 'cut') + 0.35, gain: 0.5 });
      out.push({ name: 'pop', t: c.W('s3_02', 'Freedom'), gain: 0.3 }, { name: 'paper', t: c.W('s3_03', 'publishes'), gain: 0.3 });
      ['school', 'class', 'grades'].forEach(w => out.push({ name: 'scribble', t: c.W('s3_04', w), gain: 0.18 }));
      out.push({ name: 'press', t: branch(c, every(q, 'school')) - 0.05, gain: 0.45 }, { name: 'stamp', t: branch(c, every(q, 'school')), gain: 0.45 }, { name: 'pop', t: branch(c, every(q, 'teaching')), gain: 0.3 });
      out.push({ name: 'whoosh', t: c.W('s3_05', 'Hazelwood'), gain: 0.3 }, { name: 'stamp', t: c.W('s3_05', 'Hazelwood') + 0.4, gain: 0.35 });
      out.push({ name: 'whoosh', t: c.L('s3_06') - 0.3, gain: 0.25 }, { name: 'pop', t: c.W('s3_06', 'home'), gain: 0.3 }, { name: 'press', t: c.W('s3_06', 'money'), gain: 0.5 });
      out.push({ name: 'pop', t: c.W('s3_07', 'yours'), gain: 0.3 }, { name: 'pop', t: c.W('s3_07', 'Different'), gain: 0.3 });
      return out;
    },
  };

  /* ---- s4 "Where speech stops": the megaphone, the limits, Schenck (the fire line), PROTECTED vs NOT ---- */
  const S4 = { x: 960, y: 540, w: 720, h: 560, dk: { x: 1320, y: 400, w: 800, h: 520 } };
  const LIMITS = ['TRUE THREATS', 'STIRRING UP IMMEDIATE LAWBREAKING', "LIES THAT WRECK SOMEONE'S REPUTATION", 'FRAUD'];
  function limitsList(t, tm) {
    const C = KC(), top = S4.y - S4.h / 2, L = S4.x - S4.w / 2 + 90;
    paperRect(S4.x, S4.y, S4.w, S4.h, '#fffdf7', { seed: 900, torn: true, depth: 1.4 });
    tape(S4.x, top + 2, 130, 0.03, undefined, 901);
    Civic.woodtype('NOT PROTECTED', S4.x, top + 70, 44, { color: RED, ink: C.cream, t0: tm.thr - 0.15, seed: 902, stagger: 0.03 });
    const ts = [tm.thr, tm.stir, tm.lies, tm.fraud];
    LIMITS.forEach((l, i) => {
      if (t < ts[i]) return;
      const y = top + 170 + i * 102, lines = wrapLines(l, S4.w - 150, 38, SLAB);
      cross(L - 40, y + (lines.length - 1) * 20, 0.55, P(t, ts[i], ts[i] + 0.3), 903 + i * 2);
      lines.forEach((ll, k) => hand(ll, L, y + k * 42, 38, ts[i] + k * 0.15, { font: SLAB, align: 'left', color: INK, seed: 911 + i * 2 + k, dur: 0.35 }));
    });
  }
  BODY.s4 = function (t, part, ctx, c) {
    const C = KC(), q = part.seg.question, rs = replyStart(c, q), sk = CASE('schenck'), bb = CASE('brandenburg');
    bgStreet();
    const tm = {
      mega: c.W('s4_01', 'megaphone'), upsets: c.W('s4_02', 'upsets'), thr: c.W('s4_02', 'threats'), stir: c.W('s4_02', 'stirring'),
      lies: c.W('s4_02', 'lies'), fraud: c.E('s4_02') + 0.2, fire: c.W('s4_03', 'fire'), ins: c.L('s4_04') - 0.3, dock: c.W('s4_04', 'nineteen'),
      wrong: c.W('s4_04', 'wrong'), example: c.W('s4_04', 'example'), today: c.W('s4_04', 'today'),
      rs, prot: branch(c, { q4_right: 'Critici', q4_wrong0: 'Critici', q4_wrong1: 'Disliking' }), threat: branch(c, every(q, 'threat')),
      upset2: c.W('s4_05', 'upsetting'), threatening: c.W('s4_05', 'threatening'), point: c.W('s4_06', 'point'),
    };
    const fade = 1 - P(t, rs - 0.1, rs + 0.4);
    alpha(fade, () => {
      // "Even speech that upsets people" (s4_02): still protected
      if (t > tm.upsets) tag('upsetting speech: still protected ✓', 960, 232, 36, pop(t, tm.upsets, 0.35) * (1 - P(t, tm.ins, tm.ins + 0.3)), { color: '#fffdf7', ink: GREEN, seed: 920, rot: -0.02 });
      // the limits list (s4_02), shrinking left for the docket (s4_04)
      const lp = track(t, [[0, S4.x, S4.y, 1], [tm.ins, 560, 420, 0.72, 0.7]]);
      if (t > tm.thr - 0.4) at(lp.x, lp.y, lp.s * pop(t, tm.thr - 0.4, 0.4, 1.6), S4.x, S4.y, () => limitsList(t, tm));
      // Rex: "you can't yell fire in a crowded theater" (s4_03) — struck: "people quote it wrong" (s4_04)
      const sp = track(t, [[0, 1230, 770, 1], [tm.ins, 640, 730, 0.9, 0.7]]);
      if (t > tm.fire) sticky(['"FIRE!" in a', 'crowded theater'], sp.x, sp.y, tm.fire, { w: 290, size: 36, color: '#f4c6c0', seed: 921, strike: tm.wrong, rot: 0.05 });
      // Schenck v. United States (s4_04): the exact line in small print; the sticky "an example, not the rule"
      const D = S4.dk, dx = lerp(2400, D.x, E.outCubic(P(t, tm.dock, tm.dock + 0.5)));
      if (t > tm.dock) {
        const fs = Math.round(Math.min(30, D.h * 0.07));
        const q_ = wrapLines('"' + TEXT.schenckQuote + '"', D.w - 300, fs, TYPE);
        docketAt(dx, D.y, 1, D.w, D.h, sk, { rot: 0.012, seed: 930, t0: tm.dock + 0.4, lines: ['Justice Holmes, for the Court:'].concat(q_, ['(a wartime leaflet case)']) });
        dock('schenck', [`${sk.name}, ${sk.year}, ${VOTE(sk.vote)}. Justice Holmes:`, `"${TEXT.schenckQuote}"`, `An example, not the rule. Today's rule (${bb.year}): speech aimed at immediate lawbreaking.`]);
      }
      if (t > tm.example) sticky(['an EXAMPLE,', 'not the rule;', `replaced in ${bb.year}`], 1560, 720, tm.example, { w: 290, size: 34, seed: 931, rot: -0.05 });
      if (t > tm.today) tag(`the rule since ${bb.year}: IMMEDIATE lawbreaking`, 1290, 96, 36, pop(t, tm.today, 0.35), { color: '#f2d479', ink: INK, seed: 932, rot: 0.02 });
    });
    // the reply and after: PROTECTED vs NOT PROTECTED
    const GX = 640, RX = 1290, TOP = 170, W = 580, H = 520;
    const gy = board(GX, TOP, W, H, tm.prot, 'PROTECTED', GREEN, C.cream,
      [['criticizing the mayor', tm.prot + 0.3], ['a slogan T-shirt', tm.prot + 0.55], ['an offensive opinion', tm.prot + 0.8]], { seed: 940, rot: -0.012, first: 150, pitch: 100, indent: 90 });
    const ry = board(RX, TOP, W, H, tm.threat, 'NOT PROTECTED', RED, C.cream, [['a true threat', tm.threat + 0.3]], { seed: 950, rot: 0.012, first: 150, indent: 90 });
    gy.forEach((y, i) => { const t0 = tm.prot + 0.35 + i * 0.25; if (t > t0) check(GX - W / 2 + 50, y, 0.6, P(t, t0, t0 + 0.3), 960 + i, GREEN); });
    if (t > tm.threat + 0.35) cross(RX - W / 2 + 50, ry[0], 0.6, P(t, tm.threat + 0.35, tm.threat + 0.7), 965);
    if (t > tm.upset2) inkCircle(GX - W / 2 + 90 + tw('an offensive opinion', `42px ${HANDF}`) / 2, gy[2], 180, { sx: 1.35, sy: 0.4, w: 6, color: GREEN, seed: 967, prog: P(t, tm.upset2, tm.upset2 + 0.45) });
    if (t > tm.threatening) inkCircle(RX - W / 2 + 90 + tw('a true threat', `42px ${HANDF}`) / 2, ry[0], 150, { sx: 1.3, sy: 0.45, w: 6, color: RED, seed: 968, prog: P(t, tm.threatening, tm.threatening + 0.45) });
    stampMark('THE WHOLE POINT', GX, TOP + H - 90, stampS(t, tm.point) * 0.7, { rot: -0.1, color: GREEN, size: 56 });
    // Rex's megaphone (s4_01); the crowd murmurs
    const st = stage(t, part, [[0, 'two'], [tm.ins, 'inset']]);
    cast(t, ctx, st, { front: () => { if (t > tm.mega) Civic.prop('megaphone', st.rex.x + st.rex.h * 0.34, st.rex.y - st.rex.h * 0.62, st.rex.h * 0.36, { s: pop(t, tm.mega, 0.35), rot: -0.25, seed: 970 }); } });
  };
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.6, reply: 1.6, after: 2.0 }, gaps: { s4_03: 0.4, s4_04: 0.6 } },
    sfx(part, ctx) {
      const c = cues(ctx), q = part.seg.question, out = chapterSfx(part);
      out.push({ name: fx('crowd_murmur'), t: CARD.hold + 0.2, gain: 0.35 }, { name: 'pop', t: c.W('s4_01', 'megaphone'), gain: 0.35 });
      out.push({ name: 'ding', t: c.W('s4_02', 'upsets'), gain: 0.25 });
      ['threats', 'stirring', 'lies'].forEach(w => out.push({ name: 'scribble', t: c.W('s4_02', w), gain: 0.25 }));
      out.push({ name: 'scribble', t: c.E('s4_02') + 0.2, gain: 0.2 }, { name: 'pop', t: c.W('s4_03', 'fire'), gain: 0.3 });
      out.push({ name: 'whoosh', t: c.W('s4_04', 'nineteen'), gain: 0.3 }, { name: 'stamp', t: c.W('s4_04', 'nineteen') + 0.4, gain: 0.35 });
      out.push({ name: 'scribble', t: c.W('s4_04', 'wrong'), gain: 0.35 }, { name: 'pop', t: c.W('s4_04', 'example'), gain: 0.3 }, { name: 'paper', t: c.W('s4_04', 'today'), gain: 0.3 });
      out.push({ name: 'pop', t: branch(c, { q4_right: 'Critici', q4_wrong0: 'Critici', q4_wrong1: 'Disliking' }), gain: 0.3 }, { name: 'pop', t: branch(c, every(q, 'threat')), gain: 0.3 });
      out.push({ name: 'scribble', t: c.W('s4_05', 'upsetting'), gain: 0.25 }, { name: 'scribble', t: c.W('s4_05', 'threatening'), gain: 0.25 }, { name: 'stamp', t: c.W('s4_06', 'point'), gain: 0.5 });
      return out;
    },
  };

  /* ---- s5 "More than speech": the ten amendments, Gideon / Miranda / Gault, the 9th, the club charter RATIFIED ---- */
  const S5 = { x: 580, y: 400, w: 940, h: 640, pitch: 52, dk: [[1400, 215], [1420, 400], [1440, 585]], dw: 640, dh: 280 };
  /* Florida, traced from Natural Earth 1:110m (see the header), in a 100-px box */
  const FL_LL = [[-81.5, 30.7], [-81, 29.2], [-80.5, 28.5], [-80.5, 28], [-80.1, 26.9], [-80.1, 25.8], [-80.4, 25.2], [-81.2, 25.2], [-81.3, 25.6], [-81.7, 25.9], [-82.9, 27.9], [-82.7, 28.5], [-82.9, 29.1], [-83.7, 29.9], [-84.1, 30.1], [-85.1, 29.6], [-85.8, 30.2], [-86.4, 30.4], [-87.5, 30.3], [-87.6, 31.0], [-85.0, 31.0], [-82.2, 30.6]];
  const FLORIDA = Object.assign(FL_LL.map(([lon, lat]) => [(lon + 83.8) * 0.883 * 17, -(lat - 28.1) * 17]), { k: 'fs:florida' });
  function florida(x, y, s) {
    if (s <= 0.001) return;
    paperPiece(FLORIDA, x, y, '#6f9a6a', { seed: 980, depth: 0.6, s });
  }
  function rightsScroll(t, tm) {
    const C = KC(), top = S5.y - S5.h / 2, L = S5.x - S5.w / 2;
    txt('THE BILL OF RIGHTS', S5.x - 90, top + 58, 46, { font: SLAB, color: C.navy });
    txt(`ratified ${TEXT.borYear}`, S5.x + S5.w / 2 - 60, top + 62, 28, { font: TYPE, color: C.brick, align: 'right' });
    ink([[L + 50, top + 98], [L + S5.w - 50, top + 98]], { w: 3, color: INK, seed: 981, passes: 1, step: 60 });
    const hi = { 4: [tm.mir, C.navy], 5: [tm.gid, C.gold], 8: [tm.ninth, C.brick] };
    TEN().forEach((l, i) => {
      const y = top + 126 + i * S5.pitch, t0 = tm.un + 0.3 + i * 0.15;
      if (hi[i] && t > hi[i][0]) { g.save(); g.globalAlpha *= 0.3 * P(t, hi[i][0], hi[i][0] + 0.3); g.fillStyle = hi[i][1]; g.fillRect(L + 40, y - 26, S5.w - 80, 52); g.restore(); }
      hand(ORD[i], L + 60, y, 28, t0, { font: SLAB, align: 'left', color: C.brick, seed: 982 + i, dur: 0.2 });
      hand(l, L + 150, y, fitSize(l, S5.w - 210, 34), t0 + 0.05, { align: 'left', color: INK, seed: 992 + i, dur: 0.35 });
    });
  }
  const CHARTER = { x: 960, y: 560, w: 860, h: 640 };
  BODY.s5 = function (t, part, ctx, c) {
    const C = KC();
    bgParch();
    const tm = {
      un: c.W('s5_01', 'first'), gid: c.W('s5_02', 'Gideon'), mir: c.W('s5_03', 'Miranda'), gault: c.W('s5_03', 'Gault'),
      ninth: c.W('s5_05', 'Ninth'), ch: c.L('s5_06') - 0.3, chSchool: c.W('s5_06', 'school'), chHome: c.W('s5_06', 'home'), chThreats: c.W('s5_06', 'threats'),
      rat: c.W('s5_07', 'Ratified'),
    };
    const gone = E.inOutCubic(P(t, tm.ch, tm.ch + 0.7));
    if (gone < 1) alpha(1 - gone, () => {
      g.save(); g.translate(0, -gone * 200);
      // the ten amendments (s5_01)
      const un = E.outCubic(P(t, tm.un - 0.2, tm.un + 0.7));
      scroll(S5.x, S5.y, S5.w, S5.h, un, () => rightsScroll(t, tm), 985);
      // Gideon (s5_02), Miranda, Gault (s5_03): docket sheets pinned in a cascade
      [['gideon', tm.gid, ['Clarence Gideon, of Florida:', "no lawyer; he couldn't afford one."]],
        ['miranda', tm.mir, ['Police must tell you: you can', 'stay silent and get a lawyer.']],
        ['gault', tm.gault, ['Kids accused of breaking the law', 'get a fair process too.']]].forEach(([id, t0, lines], k) => {
        if (t < t0) return;
        const K = CASE(id), [x, y] = S5.dk[k], e = E.outCubic(P(t, t0, t0 + 0.45));
        docketAt(lerp(2300, x, e), y, 1, S5.dw, S5.dh, K, { lines, eoc: false, rot: [-0.02, 0.015, -0.01][k], seed: 1000 + k * 10, t0: t0 + 0.4 });
        if (id === 'gideon') florida(lerp(2300, x, e) + S5.dw / 2 - 170, y - S5.dh / 2 + 150, 0.55 * pop(t, t0 + 0.6, 0.35));
        // the EOC badge: a small tag in each sheet's visible band (the sheet's own bottom badge would sit under the next
        // sheet, or, for the last one, under the landscape caption)
        if (K.eoc) tag('EOC', lerp(2300, x, e) + S5.dw / 2 - 70, y - S5.dh / 2 + 160, 22, pop(t, t0 + 0.5, 0.3), { font: SLAB, color: Civic.C.navy, ink: Civic.C.cream, seed: 1030 + k, rot: -0.05 });
      });
      // the 9th: the list isn't complete (s5_05), on the scroll's end
      if (t > tm.ninth) {
        const top = S5.y - S5.h / 2, y9 = top + 126 + 8 * S5.pitch;
        sticky(["9th: this list", "isn't complete"], 560, 800, tm.ninth, { w: 290, size: 36, seed: 1040, rot: -0.05 });
        inkArrow(430, 740, S5.x - S5.w / 2 + 100, y9 + 20, { w: 5, color: C.brick, seed: 1041, prog: P(t, tm.ninth + 0.3, tm.ninth + 0.7), bulge: 30, head: 18 });
      }
      g.restore();
    });
    if (gone < 0.5 && t > tm.gid) {
      const ids = [['gideon', tm.gid], ['miranda', tm.mir], ['gault', tm.gault]].filter(([, t0]) => t > t0).map(([id]) => CASE(id));
      dock('s5cases', [`Bill of Rights, ${TEXT.borYear}. ` + ids.map(K => `${K.name}, ${K.year} (${VOTE(K.vote)})`).join(' · ')]);
    }
    // Rex's club charter (s5_06), RATIFIED (s5_07)
    const cs = pop(t, tm.ch + 0.3, 0.45, 1.6);
    if (cs > 0) {
      const X = CHARTER, top = X.y - X.h / 2, L = X.x - X.w / 2 + 60;
      at(X.x, X.y, cs, X.x, X.y, () => {
        Civic.parchment(X.x, X.y, X.w, X.h, { seed: 1050, depth: 1.5 });
        txt('CORAL RIDGE COURIER CLUB', X.x, top + 70, 42, { font: SLAB, color: C.navy });
        txt('CHARTER', X.x, top + 124, 34, { font: TYPE, color: C.brick });
        ink([[L, top + 158], [X.x + X.w / 2 - 60, top + 158]], { w: 3, color: INK, seed: 1051, passes: 1, step: 60 });
        [['1. The school paper follows school rules.', tm.chSchool], ['2. My home zine says whatever I want.', tm.chHome], ['3. Except threats.', tm.chThreats]].forEach(([l, t0], i) =>
          hand(l, L, top + 226 + i * 76, fitSize(l, X.w - 120, 42), t0, { align: 'left', color: INK, seed: 1052 + i, dur: 0.7 }));
        const sy = top + X.h - 90;
        hand('signed:', L, sy - 6, 32, -1e6, { align: 'left', dur: 0.01, color: PENCIL, seed: 1056 });
        ink([[L + 110, sy + 10], [L + 470, sy + 10]], { w: 3, color: INK, seed: 1057, passes: 1, step: 60 });
        const sig = []; for (let i = 0; i <= 36; i++) { const u = i / 36; sig.push([L + 130 + u * 320, sy - 12 + Math.sin(u * 24) * 14 * (1 - u * 0.5) - u * 10]); }
        ink(sig, { w: 5, color: C.brick, seed: 1058, prog: P(t, tm.chThreats + 0.6, tm.chThreats + 1.3), step: 6, passes: 1 });
      });
      Civic.seal(X.x + X.w / 2 - 110, X.y + X.h / 2 - 110, 64, { text: 'CR', seed: 1060, s: pop(t, tm.rat + 0.35, 0.35, 2.4) });
      stampMark('RATIFIED', X.x + 170, X.y + 96, stampS(t, tm.rat) * 0.95, { rot: -0.14, size: 88 });
    }
    const st = stage(t, part, [[0, 'inset'], [tm.ch, 'two']]);
    cast(t, ctx, st);
  };
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 2.6 }, gaps: { s5_02: 0.5, s5_06: 0.9, s5_07: 0.4 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: fx('unroll', 'paper'), t: c.W('s5_01', 'first') - 0.2, gain: 0.45 });
      ['Gideon', 'Miranda', 'Gault'].forEach((w, k) => { const t0 = c.W(k ? 's5_03' : 's5_02', w); out.push({ name: 'whoosh', t: t0, gain: 0.25 }, { name: 'stamp', t: t0 + 0.4, gain: 0.35 }); });
      out.push({ name: 'pop', t: c.W('s5_05', 'Ninth'), gain: 0.35 }, { name: 'whoosh', t: c.L('s5_06') - 0.3, gain: 0.25 });
      ['school', 'home', 'threats'].forEach(w => out.push({ name: 'scribble', t: c.W('s5_06', w), gain: 0.2 }));
      const r = c.W('s5_07', 'Ratified');
      out.push({ name: 'press', t: r - 0.05, gain: 0.5 }, { name: 'stamp', t: r, gain: 0.6 }, { name: 'pop', t: r + 0.35, gain: 0.35 }, { name: fx('cheer', 'sparkle'), t: c.E('s5_07') + 0.2, gain: 0.45 });
      return out;
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part (or missing) are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5);
  }

  const FreeSpeechVideo = window.FreeSpeechVideo = { BODY, TEXT, TEN, amend1, layers: LY, lastDock: null };

  class VideoScene extends Player.Scene {
    enter() {
      super.enter();
      // bake the backdrops in spare frame time while the first chapter card is up
      if (typeof prewarm === 'function') [bgShop, bgGate, bgNews, bgStreet, bgParch].forEach(f => prewarm.draw(() => f()));
    }
  }
  Levels.video = VideoScene;
})();
